// Wire contract — sites feature INPUT schemas (project-owned, non-transferrable).
// Single source of truth consumed by backend + frontend (ADR-009). The sites
// module is the offset-pagination worked example (CONVENTIONS §Pagination) and
// the first SCD-bearing entity (site_solution_configs, ADR-056).
import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { CalendarDaySchema } from '../common/calendar-day.js';
import { IanaZoneSchema } from '../common/zone.js';
import { PaginationSchema } from '../common/pagination.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { LanguageSchema } from '../common/language.js';
import { BatteryChemistrySchema } from '../common/battery.js';
import { FixSchema } from '../common/geo.js';
import { BandKeySchema } from '../common/bands.js';
import { familiesOf } from '../observations/registry.js';
// ── shared vocab ────────────────────────────────────────────────────────────
export const SITE_STATUSES = ['draft', 'active', 'suspended', 'decommissioned'];
export const SiteStatusSchema = z.enum(SITE_STATUSES);
// ── the status FAMILIES (ems-domain DOMAIN-RULES §3) ────────────────────────
// Scope is status-blind; each SURFACE owns its status filter by picking ONE
// family. The families are defined here, once, for both ends — a surface never
// spells a population inline (the backend lint guard closes that drift):
//   record       — SITE_STATUSES, every status: the registry, search, site
//                  folders, the CMDB, chain reads, check-out, the recovery read.
//   operational  — active + suspended (post-activation, non-terminal): license
//                  seats (ADR-055), observations, the device-targets worklist,
//                  the nearby proposal, the check-in and the capture (presence
//                  is a fact; maintenance is a reason to suspend), watchlists,
//                  dispatch. A suspended site is visible and check-in-able but
//                  renders FROZEN — no dispatch, no work orders.
//   projection   — active + suspended + decommissioned (activation onward): the
//                  ems-data site dimension; terminal rows keep serving so
//                  history keeps its labels.
// Draft belongs to the record family only: mutable, hard-deletable, serves no
// feed, anchors no evidence. Lifecycle transition EDGES (activate · suspend ·
// resume · decommission) stay explicit literals — a state machine's edges are
// not a population.
export const SITE_OPERATIONAL_STATUSES = ['active', 'suspended'];
export const SiteOperationalStatusSchema = z.enum(SITE_OPERATIONAL_STATUSES);
export const isOperationalSite = (status) => SITE_OPERATIONAL_STATUSES.includes(status);
export const SITE_PROJECTION_STATUSES = ['active', 'suspended', 'decommissioned'];
// The SUPPLY sources of the ADR-056 §5 taxonomy: the on-site generator and the
// provider generator (a shared genset backing the site) are DISTINCT sources;
// battery is storage (never in the glyph strip); cooling is a load, not a source.
export const ENERGY_SOURCES = ['grid', 'solar', 'generator', 'provider_generator', 'battery', 'wind'];
export const EnergySourceSchema = z.enum(ENERGY_SOURCES);
// Battery CHEMISTRY is shared vocabulary with the CONTRACT side (the two-axis
// standard: both config tables carry chemistry only; technology grain is
// register-model truth) — canonical home: common/battery.ts; re-exported here
// so register-side consumers keep their import path.
export { BATTERY_CHEMISTRIES, BatteryChemistrySchema } from '../common/battery.js';
// The group axes — ONE list for both tabs, in the gear's order (ADR-078 §4 · ADR-040 §6: a
// grouped read answers every band with its true count and the opened bands' rows as one
// stream). solution_type = the EXACT present-supply-source set (ADR-056 §6 — compositional
// bands, no modifiers). A thousands-valued or a binary attribute is a FILTER, never an axis
// (placement is the `placement` param below).
export const SITE_GROUP_BYS = ['solution_type', 'priority', 'category', 'type', 'zone', 'cluster', 'chemistry'];
export const SiteGroupBySchema = z.enum(SITE_GROUP_BYS);
// A SOLUTION TYPE is the exact set of present supply sources (the battery is storage, the
// cooling a load — neither names a type). ONE derivation for both sides: its parts in the
// canonical order (the name's order on screen, and the band order through `bit` — the
// bit-packed ordinal the server ranks by), each with its config flag and the short code a
// band KEY is spelled with (`grid-solar-gen`; `nosource` for a config with none of them; a
// site with no configuration has no type — the band keyed `none`).
export const SOLUTION_TYPE_PARTS = [
    { flag: 'has_grid', code: 'grid', bit: 16 },
    { flag: 'has_solar', code: 'solar', bit: 8 },
    { flag: 'has_generator', code: 'gen', bit: 4 },
    { flag: 'has_provider_generator', code: 'pgen', bit: 2 },
    { flag: 'has_wind', code: 'wind', bit: 1 },
];
export const SOLUTION_TYPE_NO_SOURCE = 'nosource';
/** A solution type's band key from its flags. */
export function solutionTypeKey(has) {
    return SOLUTION_TYPE_PARTS.filter((p) => has[p.flag]).map((p) => p.code).join('-') || SOLUTION_TYPE_NO_SOURCE;
}
/** The flags a solution-type band key spells — null for a text that is no such key (a stale link, another axis). */
export function solutionTypeOfKey(key) {
    const codes = key === SOLUTION_TYPE_NO_SOURCE ? [] : key.split('-');
    const known = SOLUTION_TYPE_PARTS.map((p) => p.code);
    if (codes.some((c) => !known.includes(c)) || solutionTypeKeyOf(codes) !== key)
        return null;
    return Object.fromEntries(SOLUTION_TYPE_PARTS.map((p) => [p.flag, codes.includes(p.code)]));
}
const solutionTypeKeyOf = (codes) => SOLUTION_TYPE_PARTS.filter((p) => codes.includes(p.code)).map((p) => p.code).join('-') || SOLUTION_TYPE_NO_SOURCE;
// Where a site stands — a FILTER (a binary attribute partitions nothing worth a band).
export const SITE_PLACEMENTS = ['outdoor', 'indoor'];
export const SitePlacementSchema = z.enum(SITE_PLACEMENTS);
// 'default' = the drafts-float-to-top work-queue order (STATUS_RANK then name);
// 'queue' = the cockpit's worst-first order, ranked by observation IMPACT (no
// config → any observation → draft → clean → suspended → decommissioned); an
// explicit column sort overrides. system + the
// per-source capacity sorts (battery Ah · solar kWp · grid A · generator kVA ·
// cooling total kW) are the Solution view's.
// 'distance' = nearest first from the list's reference point (`near` — the
// common/geo list fragment); it is the one sort built per request, and it
// requires the point (400 NEAR_REQUIRED without one).
export const SITE_SORT_KEYS = ['default', 'queue', 'name', 'reference', 'refAlt', 'type', 'cluster', 'priority', 'status', 'created', 'system', 'battery', 'solar', 'grid', 'generator', 'cooling', 'distance'];
export const SiteSortKeySchema = z.enum(SITE_SORT_KEYS);
// THE COCKPIT LENS = OBSERVATION KINDS (ADR-061), not a private state list.
// It replaces review / incomplete / draft / ok: `review` was a CONFLICT and
// `incomplete` a MISSING under two other names, `draft` is a lifecycle status
// (it rides the status chips, where it already had a value), and `ok` was the
// absence of a finding, which is what you see when no lens is pressed.
//
// The consequence, deliberate: these are NOT a partition. A site can raise both
// kinds at once — three blank fields AND a drift are real work for two different
// people — so the counts OVERLAP and may sum past the row total. The old single
// CASE silently hid the second kind behind the first.
//
// Breach and unverified are not offered here: no sites family raises them (the
// registry's check that a kind is real and not a bucket).
export const SITE_FACES = ['registry', 'solution'];
export const SiteFaceSchema = z.enum(SITE_FACES);
export const SITE_LENS_KINDS = ['conflict', 'missing'];
export const SiteLensKindSchema = z.enum(SITE_LENS_KINDS);
export const SITE_OBSERVATION_FAMILIES = familiesOf('sites');
export const SiteObservationFamilySchema = z.enum(SITE_OBSERVATION_FAMILIES);
export const SITE_ENVIRONMENTS = ['indoor', 'outdoor'];
export const SiteEnvironmentSchema = z.enum(SITE_ENVIRONMENTS);
// Solution is filtered by SOURCE PRESENCE (single-select per source), never by
// exact archetype — the archetype is the GROUP axis. `grid` is on/off-grid;
// solar / generator (on-site) / provider_gen are yes/no. Battery is not a
// filter (near-universal → no signal).
export const GRID_FILTER_STATES = ['offgrid', 'ongrid'];
export const GridFilterSchema = z.enum(GRID_FILTER_STATES);
export const SOURCE_PRESENCE = ['yes', 'no'];
export const SourcePresenceSchema = z.enum(SOURCE_PRESENCE);
// CSV multi-value filter param — canonical home: common/query.ts (promoted on
// the third consumer, the projections reads).
import { csv } from '../common/query.js';
import { GeoListQueryShape } from '../common/geo.js';
import { TerritoryParamSchema } from '../common/territory.js';
// ── list ────────────────────────────────────────────────────────────────────
export const SitesListQuerySchema = PaginationSchema.extend({
    q: z.string().trim().max(64).optional(),
    status: csv(SiteStatusSchema),
    // The two PLACE filters. `territory` — ONE kinded param (common/territory.ts:
    // `zone:<id>` · `cluster:<id>`, the picks adding up, the highest pick alone);
    // `administrative_area_id` — each pick means everything INSIDE it (ADR-080 §14:
    // in a filter a level means everything within), expanded to its subtree by the read.
    territory: TerritoryParamSchema,
    administrative_area_id: csv(z.coerce.number().int().positive()),
    site_type_id: csv(z.coerce.number().int().positive()),
    site_priority_id: csv(z.coerce.number().int().positive()),
    site_category_id: csv(z.coerce.number().int().positive()),
    tag_id: csv(z.coerce.number().int().positive()),
    placement: SitePlacementSchema.optional(), // outdoor | indoor
    // Solution by source presence (the filter lens); the archetype is the group lens.
    grid: GridFilterSchema.optional(), // off-grid | on-grid
    solar: SourcePresenceSchema.optional(), // solarized | not
    generator: SourcePresenceSchema.optional(), // has an ON-SITE generator | not
    provider_gen: SourcePresenceSchema.optional(), // backed by a provider generator | not
    cooling: SourcePresenceSchema.optional(), // has a cooling load | not (a LOAD, not a source — Solution view)
    chemistry: csv(BatteryChemistrySchema), // current config's battery chemistry (Solution view)
    provider: csv(z.string().trim().max(64)), // current config's grid provider (Solution view)
    queue: csv(SiteLensKindSchema), // the cockpit's KIND lens (composes AFTER the shared filters)
    // The FAMILY dimension (ADR-061 two levels): OR-union like the lens; a family
    // implies its own predicate, so cross-face values stay honest row predicates.
    observation: csv(SiteObservationFamilySchema),
    // WHICH FACE the lens works. Sites has two — the site's own record (registry)
    // and the declared solution config (solution) — exactly like the asset
    // register's ops/books split, and each tab may only filter its OWN findings.
    face: SiteFaceSchema.default('solution'),
    // The reference point + radius (ADR-071) — rows gain `distance_m` when a
    // point is given; `radius_m` and `sort_by=distance` require it.
    ...GeoListQueryShape,
    sort_by: SiteSortKeySchema.default('default'),
    sort_dir: z.enum(['asc', 'desc']).default('asc'),
    // A GROUPED read (ADR-078 §4): the answer gains `bands` — every band of the filtered
    // population with its true count, in the axis's order — and `items` is the STREAM: the
    // rows of the bands the person opened, in band order then the sort, paged like the plain
    // list, each row stamped `band`.
    group_by: SiteGroupBySchema.optional(),
    // The opened bands' keys (`*` = every band; absent = folded — the arrival). A key that
    // names no band is a tolerant read (the server answers the bands it has). Without
    // `group_by` the open set is ignored (the count face shares this schema).
    open: csv(BandKeySchema),
}).strict();
// ── create (draft-first: name + cluster + reference — ADR-056 §4 revised) ────
// `reference` is the site's stable business IDENTITY (the import/export match key
// everywhere), so it is required even for a draft; `reference_alt` stays optional.
export const CreateSiteRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(32),
    cluster_id: z.number().int().positive(),
    reference: z.string().trim().min(1).max(32),
    reference_alt: z.string().trim().min(1).max(32).optional(),
})
    .strict();
// ── update (partial; nullable fields may be cleared while draft) ─────────────
// The priced designations (type · priority · category) are NOT here — they are
// SCD-managed (site_designations, ADR-060 §3): every write, draft included, goes
// through the designation verbs below, which maintain the sites mirror columns.
export const UpdateSiteRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(32).optional(),
    reference: z.string().trim().min(1).max(32).nullable().optional(),
    reference_alt: z.string().trim().min(1).max(32).nullable().optional(),
    cluster_id: z.number().int().positive().optional(),
    is_indoor: z.boolean().optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
    country_iso_code: z.string().trim().length(2).nullable().optional(),
    // The site's zone — the one zone check (ADR-082 §3): an IANA name both runtimes accept, never an offset.
    timezone_iana: IanaZoneSchema.nullable().optional(),
    administrative_area_id: z.number().int().positive().nullable().optional(),
    tag_ids: z.array(z.number().int().positive()).optional(),
})
    .strict();
// ── solution config values (the ADR-056 §5 taxonomy — presence + site TOTALS) ─
// "Which fields are required when a source is enabled" stays soft here (draft
// configs stay partial) — completeness is computed + tracked as Missing, never a
// write gate (ADR-056 §4 permissive-gate / strict-tracking split).
export const SolutionConfigValuesSchema = z
    .object({
    has_grid: z.boolean().default(false),
    has_solar: z.boolean().default(false),
    has_generator: z.boolean().default(false),
    has_provider_generator: z.boolean().default(false),
    has_battery: z.boolean().default(false),
    has_wind: z.boolean().default(false),
    power_system_capacity_kw: z.number().positive().nullable().optional(),
    battery_capacity_ah: z.number().int().positive().nullable().optional(),
    battery_chemistry: BatteryChemistrySchema.nullable().optional(),
    solar_capacity_kwp: z.number().positive().nullable().optional(),
    generator_capacity_kva: z.number().positive().nullable().optional(),
    generator_units: z.number().int().positive().nullable().optional(),
    generator_tank_l: z.number().int().positive().nullable().optional(),
    // The provider generator's capacity is the provider's asset — never stored.
    provider_generator_name: z.string().trim().min(1).max(64).nullable().optional(),
    provider_generator_auto_failover: z.boolean().default(false),
    wind_capacity_kw: z.number().positive().nullable().optional(),
    grid_provider: z.string().trim().max(64).nullable().optional(),
    grid_capacity_kw: z.number().positive().nullable().optional(),
    has_cooling: z.boolean().default(false),
    cooling_units: z.number().int().positive().nullable().optional(),
    cooling_capacity_kw: z.number().positive().nullable().optional(), // site TOTAL
})
    .strict();
// ── solution config write (create the FIRST period — onboarding / gap-fill on a
// not-configured site). Once a timeline exists, every mutation goes through the
// four verbs below (the register holds only real periods — ADR-056 §2).
export const SolutionConfigRequestSchema = SolutionConfigValuesSchema.extend({
    ...stampedBody,
    effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD').optional(),
    change_reason: z.string().trim().max(500).nullable().optional(),
}).strict();
// ── the timeline verbs (ADR-056 §2 — change · correct data · correct start
// date · delete; the user only ever answers "since when?") ───────────────────
/** "The solution changed" — a real change since any date ≤ today; an old date
 *  splits the period containing it (recording late is first-class). */
export const SolutionChangeRequestSchema = SolutionConfigValuesSchema.extend({
    effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD'),
    change_reason: z.string().trim().max(500).nullable().optional(),
}).strict();
/** "Correct data" — repair a period's values in place; no new period, no dates.
 *  The fix surfaces via audit_logs (who-knew-when). */
export const SolutionCorrectionRequestSchema = SolutionConfigValuesSchema.extend(stampedBody);
/** "Correct start date" — a boundary is ONE fact: the previous period's close
 *  follows silently; a boundary never leapfrogs its neighbours. */
export const StartDateCorrectionRequestSchema = z
    .object({ ...stampedBody, effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD') })
    .strict();
// ── designation values (the priced designations — ADR-060 §3) ────────────────
// A period is the COMPLETE classification state: type + priority always stated
// (NOT NULL); category is REQUIRED-NULLABLE — the caller states `null` ("no
// category") explicitly, so an omitted field can never silently clear one.
export const DesignationValuesSchema = z
    .object({
    site_type_id: z.number().int().positive(),
    site_priority_id: z.number().int().positive(),
    site_category_id: z.number().int().positive().nullable(),
})
    .strict();
// ── designation write (create the FIRST period). The classification timeline
// starts at FIRST CLASSIFICATION, drafts included (no regime split at
// activation). Mirrors the solution PUT: effective_from optional (default
// today), backdatable ≤ today.
export const DesignationsRequestSchema = DesignationValuesSchema.extend({
    ...stampedBody,
    effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD').optional(),
    change_reason: z.string().trim().max(500).nullable().optional(),
}).strict();
/** "The classification changed" — since any date ≤ today; an old date splits the
 *  period containing it (backdating is first-class — ADR-060 §3). */
export const DesignationChangeRequestSchema = DesignationValuesSchema.extend({
    effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD'),
    change_reason: z.string().trim().max(500).nullable().optional(),
}).strict();
/** "Correct data" — repair a period's classification in place; no new period.
 *  (Start-date corrections reuse StartDateCorrectionRequestSchema.) */
export const DesignationCorrectionRequestSchema = DesignationValuesSchema.extend(stampedBody);
// ── lifecycle ────────────────────────────────────────────────────────────────
// Every step carries its DECLARED calendar day — "as of when did this happen":
// the activation day ("active since" — the platform's operational start, its
// own fact beside the solution's service start), the pause's start, its end,
// the retirement day. Optional: absent = today (the day of the act); never in
// the future; ordered as the machine walks (activated ≤ suspended ≤ resumed;
// activated ≤ decommissioned, after any pause) — `INVALID_LIFECYCLE_DATE
// { field, reason }` otherwise. Stored as the sites `_on` date columns (plain
// YYYY-MM-DD end to end); the instant of the act is the audit row's.
const LifecycleDay = CalendarDaySchema; // a REAL day — `2024-02-31` is a 400 here, never a 500 at the column
export const LIFECYCLE_DAY_FIELDS = ['activated_on', 'suspended_on', 'resumed_on', 'decommissioned_on'];
export const ActivateSiteRequestSchema = z.object({ activated_on: LifecycleDay.optional() }).strict();
export const SuspendSiteRequestSchema = z
    .object({ suspend_reason: z.string().trim().min(1).max(500), suspended_on: LifecycleDay.optional() })
    .strict();
export const ResumeSiteRequestSchema = z.object({ resumed_on: LifecycleDay.optional() }).strict();
export const DecommissionSiteRequestSchema = z.object({ decommissioned_on: LifecycleDay.optional() }).strict();
/** "Correct a recorded day" — repairs the days the row already HOLDS, in place
 *  (a draft has none; a never-paused site has no pause to date). At least one
 *  field; the same ordering law; audited `lifecycle_dates_corrected` (a repair,
 *  never a transition). */
export const LifecycleDatesCorrectionRequestSchema = z
    .object({
    ...stampedBody,
    activated_on: LifecycleDay.optional(),
    suspended_on: LifecycleDay.optional(),
    resumed_on: LifecycleDay.optional(),
    decommissioned_on: LifecycleDay.optional(),
})
    .strict()
    .refine((b) => LIFECYCLE_DAY_FIELDS.some((f) => b[f] !== undefined), { message: 'at least one date is required' });
// ── import/export (ADR-041/045/047; the sites datasets) ─────────────────────
// ── the lifecycle import (the `transition` op — ADR-047) ─────────────────────
// One row = one state-machine step on an existing site: `reference · action ·
// date · suspend_reason`. Three actions — decommission is deliberately NOT in
// the file (terminal, low-frequency: a UI act). A row with NO action is a
// pure-identity row — the site is left as it is (a skip): a round-tripped
// current-data file states steps only where the user typed one, the engine's
// absent = untouched grammar. `date` is the day the step took effect — any
// action; absent = today; the same ordering law as the buttons. A site ALREADY
// at the action's target status with a `date` that differs from its recorded day
// is a CORRECTION of that day (preview shows old → new, audited
// `lifecycle_dates_corrected`); the same or no date = skip. A `date` with NO
// action is the day of the site's CURRENT status (active → activated_on ·
// suspended → suspended_on · decommissioned → decommissioned_on — a draft has
// none): the same correction-or-skip rule. The one repair the op performs.
// `suspend_reason` is REQUIRED on a suspend row and must be EMPTY on any other
// (the column-model enforces both — a value the record cannot store is never
// silently dropped).
export const SITE_LIFECYCLE_ACTIONS = ['activate', 'suspend', 'resume'];
export const SiteLifecycleActionSchema = z.enum(SITE_LIFECYCLE_ACTIONS);
export const SiteLifecycleImportRowSchema = z
    .object({
    reference: z.string().trim().min(1).max(32),
    action: SiteLifecycleActionSchema.optional(), // absent = leave the site as it is
    date: LifecycleDay.optional(),
    suspend_reason: z.string().trim().min(1).max(500).optional(),
})
    .strict();
// Current-view export: the list's filter set (no pagination — the file is the
// FULL filtered set; no group_by — grouping is a screen affordance). `sort_by`
// rides so the file mirrors the on-screen order. Serves the register AND the
// solutions dataset (same site-narrowing filters, different columns).
export const ExportSitesQuerySchema = SitesListQuerySchema.omit({
    limit: true,
    offset: true,
    group_by: true,
    open: true,
})
    .extend({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
})
    .strict();
// One register import row — the match key is `reference` (the site's stable
// business identity; ADR-056 §4 revised). References speak NAMES (cluster / type
// / priority / tags) or the administrative-area PATH; the column-model resolves them to ids
// per request (reject-on-miss). Every field except `reference` is optional: an
// ABSENT column leaves the field untouched on a matched site. `name` (editable,
// still unique) and `cluster` are required when the row CREATES a site
// (validateRow); import never CHANGES the reference (the key) — that is an
// in-app edit.
export const SiteImportRowSchema = z
    .object({
    reference: z.string().trim().min(1).max(32),
    name: z.string().trim().min(1).max(32).optional(),
    reference_alt: z.string().trim().min(1).max(32).optional(),
    cluster: z.string().trim().min(1).max(32).optional(),
    // The designations (type · priority · category) are READ-ONLY echo columns on
    // the register file — the designations dataset owns classification.
    // The administrativeArea breadcrumb "Parent / Child" (the administrative-areas-import path
    // format), or a bare unique name.
    administrative_area: z.string().trim().min(1).max(255).optional(),
    environment: SiteEnvironmentSchema.optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    // ISO-2 code OR the country's display name in EN/FR/AR (ADR-059 §8) — the
    // column-model resolves either form against the project whitelist.
    country_iso_code: z.string().trim().min(2).max(64).optional(),
    // The one zone check (ADR-082 §3) — the row's message is the same code the forms refuse with, once per cell.
    timezone_iana: IanaZoneSchema.optional(),
    // '>'-separated tag names; the cell IS the site's tag set (PUT semantics —
    // removals surface as a preview warning). Absent column = tags untouched.
    tags: z.array(z.string().trim().min(1).max(64)).optional(),
})
    .strict();
// One dated-change solutions row: "these values, as of effective_from" (the
// `change` op). The site identity is its `reference` (ADR-056 §4 revised — the
// stable key everywhere). NOTHING else is mandatory — drafts have nothing
// mandatory, only format validation: `effective_from` is optional (no
// date + no timeline → first period today, mirroring the UI PUT default; no
// date + a timeline → a correction of the current period, mirroring the UI's
// dateless correct-verb); value fields are optional WITHOUT defaults — an
// absent column inherits the current period's value (a change states only what
// changed); on a first period, absent means the schema default (false / empty).
export const SiteSolutionImportRowSchema = z
    .object({
    reference: z.string().trim().min(1).max(32),
    effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD').optional(),
    has_grid: z.boolean().optional(),
    grid_provider: z.string().trim().max(64).optional(),
    grid_capacity_kw: z.number().positive().optional(),
    has_solar: z.boolean().optional(),
    solar_capacity_kwp: z.number().positive().optional(),
    has_generator: z.boolean().optional(),
    generator_capacity_kva: z.number().positive().optional(),
    generator_units: z.number().int().positive().optional(),
    generator_tank_l: z.number().int().positive().optional(),
    has_provider_generator: z.boolean().optional(),
    provider_generator_name: z.string().trim().min(1).max(64).optional(),
    provider_generator_auto_failover: z.boolean().optional(),
    has_battery: z.boolean().optional(),
    battery_chemistry: BatteryChemistrySchema.optional(),
    battery_capacity_ah: z.number().int().positive().optional(),
    has_wind: z.boolean().optional(),
    wind_capacity_kw: z.number().positive().optional(),
    has_cooling: z.boolean().optional(),
    cooling_units: z.number().int().positive().optional(),
    cooling_capacity_kw: z.number().positive().optional(),
    power_system_capacity_kw: z.number().positive().optional(),
})
    .strict();
// One designations row: "this classification, as of effective_from" (the
// import cut — the designations dataset owns classification at scale, ALL
// statuses; the register's designation columns are read-only echoes).
// References speak NAMES (type / priority / category, CI). Same one teachable
// rule as solutions: no timeline → first values (dateless = today); a date
// after the current start → change; no date / the current start's date on a
// classified site → correction of the current period; a date before the
// current start → rejected (history rewrites are UI surgery); identical →
// skip. An absent value column INHERITS the current period's value; on a first
// period type + priority are required (the classification state is complete by
// construction). change_reason is offered (unlike solutions) — designation
// changes are commercial acts where a shared batch reason is the norm.
export const SiteDesignationImportRowSchema = z
    .object({
    reference: z.string().trim().min(1).max(32),
    effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD').optional(),
    site_type: z.string().trim().min(1).max(64).optional(),
    site_priority: z.string().trim().min(1).max(64).optional(),
    site_category: z.string().trim().min(1).max(64).optional(),
    change_reason: z.string().trim().max(500).optional(),
})
    .strict();
// (The site-assignments import row schema is DYNAMIC — one ref_list column per
// responsibility — so it is built per request by core/accountability's
// assignmentMatrix, not declared here; ADR-059.)
// GET …/sites/assignments/export — the coverage matrix, mirroring the CURRENT
// VIEW like every other sites dataset (ADR-045): the full sites filter set +
// `q` (the list-parity predicate over name/reference/alt-reference/tags via
// filteredSites) select WHICH site rows appear; the matrix stays self-bounding
// to the sites the actor can read.
export const ExportSiteAssignmentsQuerySchema = ExportSitesQuerySchema;
// GET …/sites/export/combined — several sites datasets in ONE workbook (XLSX
// only — CSV cannot carry sheets; ADR-059). `datasets` arrives comma-separated;
// the service re-asserts each dataset's own export gate. Site-narrowing filters
// (the view state) ride unchanged and bound EVERY selected sheet, assignments
// included (the same filteredSites id set).
export const SitesCombinedDatasetSchema = z.enum(['register', 'solutions', 'assignments']);
export const ExportSitesCombinedQuerySchema = ExportSitesQuerySchema.omit({ format: true })
    .extend({
    datasets: z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(SitesCombinedDatasetSchema).min(1).max(3)),
})
    .strict();
// ── nearby (the check-in preview — the site pick's sibling) ─────────────────
// A POST-shaped read: a person's position never rides an unredacted log — the
// ceremony's fix travels in a body; the list's `near` (common/geo) is the one
// URL-borne point, masked in the request log (core/logger.ts). The server ranks the viewer's own scoped sites by straight-line
// distance and judges each against the project's tolerance — the client
// computes nothing, and holds no rule: HOW FAR is "near" is the project's
// Files › GPS setting (`visit_nearby_radius_m`), never a request parameter.
// `limit` is only how many rows the screen can show. `site_id` = "the person
// picked this one first": its own distance rides `picked`, whatever the radius.
export const NEARBY_LIMIT_MAX = 20;
export const SitesNearbyRequestSchema = z
    .object({
    fix: FixSchema,
    site_id: z.string().min(1).max(16).optional(),
    limit: z.number().int().positive().max(NEARBY_LIMIT_MAX).default(5),
})
    .strict();
